import { useMemo, useState } from 'react';
import { View } from 'react-native';
import { useRouter } from 'expo-router';
import { FirebaseError } from 'firebase/app';
import { reload, sendEmailVerification, signOut } from 'firebase/auth';
import { Screen } from '@/components/layout/Screen';
import { Button } from '@/components/ui/Button';
import { Body, Caption } from '@/components/ui/Text';
import { auth } from '@/lib/firebase';
import { useTheme } from '@/theme';
import { useUserStore } from '@/state/userStore';
import { humanizeAsyncError } from '@/utils/asyncErrorMessage';
import { getEmailVerificationActionCodeSettings } from '@/utils/emailVerificationActionSettings';

export default function VerifyEmailScreen() {
  const router = useRouter();
  const theme = useTheme();
  const authReady = useUserStore((state) => state.authReady);
  const isAuthenticated = useUserStore((state) => state.isAuthenticated);
  const hasCompletedOnboarding = useUserStore((state) => state.hasCompletedOnboarding);
  const fallbackEmail = useUserStore((state) => state.firebaseUser?.email ?? '');
  const setNeedsEmailVerification = useUserStore((state) => state.setNeedsEmailVerification);

  const [checking, setChecking] = useState(false);
  const [resending, setResending] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [message, setMessage] = useState<string | undefined>();

  const displayEmail = useMemo(() => {
    return auth.currentUser?.email ?? fallbackEmail ?? '';
  }, [fallbackEmail]);

  const routeAfterVerification = () => {
    if (hasCompletedOnboarding) {
      router.replace('/(tabs)');
      return;
    }
    router.replace('/(onboarding)/profile-details');
  };

  const handleIHaveVerified = async () => {
    if (!auth.currentUser) {
      setError('Your session expired. Please sign in again.');
      return;
    }

    setChecking(true);
    setError(undefined);
    setMessage(undefined);
    try {
      await reload(auth.currentUser);
      const verified = auth.currentUser.emailVerified === true;
      setNeedsEmailVerification(!verified);
      if (verified) {
        routeAfterVerification();
      } else {
        setMessage('Your email is not verified yet. Open the verification link and try again.');
      }
    } catch (err) {
      setError(humanizeAsyncError(err, 'We could not verify your email status right now. Please try again.'));
    } finally {
      setChecking(false);
    }
  };

  const handleResend = async () => {
    if (!auth.currentUser) {
      setError('Your session expired. Please sign in again.');
      return;
    }

    setResending(true);
    setError(undefined);
    setMessage(undefined);
    try {
      await sendEmailVerification(auth.currentUser, getEmailVerificationActionCodeSettings());
      setMessage('Verification email sent. Check your inbox and spam folder.');
    } catch (err) {
      if (err instanceof FirebaseError) {
        console.error('Resend verification email failed', {
          code: err.code,
          message: err.message,
          projectId: auth.app.options.projectId,
          authDomain: auth.app.options.authDomain,
          userEmail: auth.currentUser?.email,
        });
      }
      setError(
        humanizeAsyncError(
          err,
          'We could not resend the verification email right now. Please try again.',
        ),
      );
    } finally {
      setResending(false);
    }
  };

  if (!authReady) {
    return null;
  }

  if (!isAuthenticated) {
    return (
      <Screen
        title="Sign in required"
        subtitle="Please sign in to continue with email verification."
        scrollable={false}
      >
        <Button
          label="Back to sign in"
          onPress={() => router.replace('/(onboarding)/sign-in')}
        />
      </Screen>
    );
  }

  return (
    <Screen
      title="Verify your email"
      subtitle="To protect your account, confirm your email address before continuing."
      scrollable={false}
    >
      <View style={{ gap: theme.spacing(2) }}>
        <Body>
          Verify {displayEmail || 'your email address'} to continue.
        </Body>
        <Caption color={theme.colors.textSecondary}>
          Tap "Resend verification email" if you do not see it yet, then return here after opening the link.
        </Caption>
        {error ? <Caption color={theme.colors.error}>{error}</Caption> : null}
        {message ? <Caption color={theme.colors.textSecondary}>{message}</Caption> : null}
        <Button
          label="I have verified my email"
          onPress={handleIHaveVerified}
          loading={checking}
          disabled={checking || resending}
        />
        <Button
          label="Resend verification email"
          variant="secondary"
          onPress={handleResend}
          loading={resending}
          disabled={checking || resending}
        />
        <Button
          label="Use a different account"
          variant="ghost"
          onPress={() => {
            void signOut(auth);
          }}
          disabled={checking || resending}
        />
      </View>
    </Screen>
  );
}
